/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext } from "react";
import { useHistory } from 'react-router-dom';

import { Formik } from "formik";

import { GlobalLoaderContext, GlobalAuthContext } from "store/context";
import { LoaderContext } from '@fitinsur/contexts';
import { useTextMessage } from '@fitinsur/hooks';
import { FitinsurAPI, MessageCenter } from "utils";

import {
  FormContainer,
  Button,
  ContentCard,
  Loader
} from "@fitinsur/components";

import Layout from 'components/Layout';

import Row from "components/Grid/Row";
import { ButtonContainer } from '@fitinsur/components/Button';
import Colors from "constants/Colors";
import {
  ButtonContent,
  FieldText,
  DropzoneContainer,
  UploadMessage
} from "./styles";

import ImportPreview from './ImportPreview';

import Dropzone from 'react-dropzone';
import * as XLSX from 'xlsx';
import { intersection, orderBy } from 'lodash';
import moment from 'moment';

import { useSaveEconomicIndexersFile, useGetEconomicIndexersFile } from "../../services/EconomicIndexesService";

import { useGetTextMessageByDomainType } from "../../services/TextMessageService";

const { REACT_APP_BASE_URL_API } = process.env;

export default function ManagementEconomicIndexersImport({ permissions, match, ...props }) {
  const history = useHistory();

  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const fitinsur = new FitinsurAPI();

  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const { getDescription } = useTextMessage();

  const mc = new MessageCenter();

  const [validation, setValidation] = useState(false);
  const [indexersRates, setIndexersRates] = useState([]);
  const [updatedFileData, setUpdatedFileData] = useState(null);
  const [defaultIndexersList, setDefaultIndexersList] = useState([]);

  const defaultColumnsFile = [
    'ACRONYM_INDEXER',
    'YEAR',
    'MONTH',
    'EX_RATE'
  ];

  const {
    request: useSaveEconomicIndexersFileRequest,
    loading: useSaveEconomicIndexersFileLoading,
  } = useSaveEconomicIndexersFile();

  const {
    request: useGetEconomicIndexersFileRequest,
    loading: useGetEconomicIndexersFileLoading,
  } = useGetEconomicIndexersFile(updatedFileData?.documentId);

  const {
    request: useGetTextMessageByDomainTypeRequest,
    loading: useGetTextMessageByDomainTypeLoading,
  } = useGetTextMessageByDomainType(62); // pending ENUM to remove magical numbers

  useEffect(() => {
    updateIsLoadingStatus(
      useSaveEconomicIndexersFileLoading ||
      useGetEconomicIndexersFileLoading ||
      useGetTextMessageByDomainTypeLoading
    )
  }, [
    useSaveEconomicIndexersFileLoading, useGetEconomicIndexersFileLoading, useGetTextMessageByDomainTypeLoading
  ]);

    useEffect(() => {
      useGetTextMessageByDomainTypeRequest({}, { isoCode: "pt-BR", modelName: "DOMAINS"}).then(response => {
        if (response.status === 200) {
          setDefaultIndexersList(response.data.value.map(indexer => { 
            return {
              rateName: indexer.translatedDescription.toUpperCase(), idDomain: indexer.modelItemId
            }
          }));
        }
      });
    }
    , []);
  const renderDragMessage = (isDragActive, isDragReject) => {
    if (!isDragActive)
      return <UploadMessage>{getDescription("DOMAINS", 1129)} </UploadMessage>;

    if (isDragReject)
      return <UploadMessage type="error">{getDescription("DOMAINS", 1130)}</UploadMessage>;

    return <UploadMessage type="success">{getDescription("DOMAINS", 1131)}</UploadMessage>;
  };

  const handleFileImported = async (file) => {
    const workBook = XLSX.read(file, { cellDates: true, dateNF: 'mm/dd/yyyy;@' });

    let indexesRates = XLSX.utils.sheet_to_json(workBook.Sheets['índices'], { defval: null, raw: false });

    const defaultIndexersListName = defaultIndexersList.map(indexer => indexer.rateName);

    indexesRates = indexesRates.filter(rate => {
      const columns = Object.keys(rate).map(v => v.trim()).sort();
      return JSON.stringify(defaultColumnsFile) === JSON.stringify(intersection(defaultColumnsFile, columns));
    });

    if (!indexesRates.length)
      return mc.error(getDescription("DOMAINS", 1132));

    function validateMonth(month) {
      return (month.length === 1 || month.length === 2) &&
        (Number(month) >= 1 && Number(month) <= 12);
    }

    const formattedIndexesRates = indexesRates.map((rate) => {
      const hasRate = indexesRates.find(indexRate => indexRate.ACRONYM_INDEXER === rate.ACRONYM_INDEXER && indexRate.YEAR === rate.YEAR && indexRate.MONTH === rate.MONTH && indexRate.__rowNum__ != rate.__rowNum__)
      
      //
      if (hasRate) {
        rate.error = `Linha ${ Number(rate.__rowNum__) + 1} duplicada`;
      }

      let validateMonthAndYear = true;
      if (!(rate.ACRONYM_INDEXER && defaultIndexersListName.includes(rate.ACRONYM_INDEXER.toUpperCase()))) {
        rate.error = getDescription("DOMAINS", 1133).replace('@@defaultColumnsFile', defaultColumnsFile[0]).replace('@@rowNum', Number(rate.__rowNum__) + 1);
      } else if (!(rate.YEAR && rate.YEAR.length === 4 && !!Number(rate.YEAR))) {
        rate.error = getDescription("DOMAINS", 1133).replace('@@defaultColumnsFile', defaultColumnsFile[1]).replace('@@rowNum', Number(rate.__rowNum__) + 1);
        validateMonthAndYear = false;
      } else if (!(rate.MONTH && !!Number(rate.MONTH) && validateMonth(rate.MONTH))) {
        rate.error = getDescription("DOMAINS", 1133).replace('@@defaultColumnsFile', defaultColumnsFile[2]).replace('@@rowNum', Number(rate.__rowNum__) + 1);
        validateMonthAndYear = false;
      } else if (rate.EX_RATE == null || rate.EX_RATE?.trim("") == "" || isNaN(Number(rate.EX_RATE.replace(',', '.')))) {
        rate.error = getDescription("DOMAINS", 1133).replace('@@defaultColumnsFile', defaultColumnsFile[3]).replace('@@rowNum', Number(rate.__rowNum__) + 1);
      }

      const formattedDate = validateMonthAndYear ?
        moment().set({
          year: rate.YEAR,
          month: rate.MONTH - 1,
          date: 1,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0
        }).format() : null;

      if (formattedDate && moment(formattedDate).isAfter(moment())) {
        rate.error = getDescription("DOMAINS", 1165);
      }

      return {
        indexerCode: rate.ACRONYM_INDEXER.trim().toUpperCase(),
        indexerRate: rate.EX_RATE == null ? '-' : Number(rate.EX_RATE ? rate.EX_RATE.replace(',', '.') : ''),
        idDomain: defaultIndexersList.find(indexer => indexer.rateName === (rate.ACRONYM_INDEXER || '').toUpperCase())?.idDomain,
        date: formattedDate,
        year: rate.YEAR,
        month: rate.MONTH,
        error: rate.error ? rate.error : 'OK'
      }
    });

    const indexesRatesOrdering = orderBy(formattedIndexesRates, ['date', 'indexerCode'], ['asc', 'asc']);

    const errorsValidation = !!formattedIndexesRates.find(rate => rate.error !== 'OK');

    setValidation(!errorsValidation);

    setIndexersRates(indexesRatesOrdering);
  }

  const handleImport = async (files) => {
    loading.start();
    await handleUploadFile(files[0]);
    const file = await files[0].arrayBuffer();
    await handleFileImported(file);
    loading.stop();
  }

  const handleUploadFile = async (paramFile) => {
    const formData = new FormData();

    formData.append('files', paramFile);
    formData.append('idOperation', "");
    formData.append('idTypeDocument', "");
    formData.append('dsTypeDocument', "");
    formData.append('isDraft', "true");
    formData.append('isInternalDocument', "");

    fitinsur.setAuthToken(authContext.token);
    const response = await fitinsur.requestFileUpload(formData).then(response => response.json());
    if (response.ret_code !== 200) {
      loading.stop();
      mc.error(response.ret_msg_details);
      return;
    }

    const [file] = response.ret_value;

    setUpdatedFileData(file)
  }

  const handleRejectEvent = event => {
    if (event.length > 1)
      return mc.error(getDescription("DOMAINS", 1134));

    if (event[0].errors[0].code === 'file-invalid-type')
      return mc.error(getDescription("DOMAINS", 1135));
  }

  const importIndexersRates = async () => {
    try {
      fitinsur.setAuthToken(authContext.token);

      await useSaveEconomicIndexersFileRequest({
        indexersRates,
        documentId: updatedFileData.documentId
      });

      setIndexersRates([]);

      await useGetEconomicIndexersFileRequest();

      setUpdatedFileData(null);
      setValidation(false);
      mc.success(getDescription("DOMAINS", 1136));

      history.goBack();
    } catch (err) {
      mc.error(getDescription("DOMAINS", 1137));
    };
  }

  return (
    <Formik>
      <Layout
        idOperation={props?.idOperation}
        title={getDescription("DOMAINS", 1122)}>
        <ContentCard hide>
          <Dropzone
            onDropAccepted={handleImport}
            onDropRejected={handleRejectEvent}
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">
            {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
              <DropzoneContainer
                {...getRootProps()}
                isDragActive={isDragActive}
                isDragReject={isDragReject}
              >
                <input {...getInputProps()} />
                {renderDragMessage(isDragActive, isDragReject)}
              </DropzoneContainer>
            )}
          </Dropzone>
          <Row>
            <FieldText size={12}>
              {getDescription("DOMAINS", 1138)}&nbsp;
              <a
                style={{ textDecoration: 'none' }}
                href={`${String(REACT_APP_BASE_URL_API)}/staticfile?filename=model_import_indexers_rates.xlsx`}
              >
                &nbsp;XLSX
              </a>
            </FieldText>
          </Row>
          {indexersRates.length ?
            (
              <FormContainer title={getDescription("DOMAINS", 1139)}>
                <ImportPreview file={indexersRates} />
              </FormContainer>
            ) : ''
          }
          <ButtonContainer>
            <Button forth onClick={() => history.goBack()}>&lt; {getDescription("DOMAINS", 758)}</Button>
            <ButtonContent>
              {validation &&
                <Button
                  type="submit"
                  primary
                  onClick={() => importIndexersRates()}
                >
                  {getDescription("DOMAINS", 1140)}
                </Button>
              }
            </ButtonContent>
          </ButtonContainer>
          {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
        </ContentCard>
      </Layout>
    </Formik>
  );
};